import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from "axios";
import config from "../config/config";

// Create axios instance for unauthorized requests
export const axiosUnauthorized: AxiosInstance = axios.create({
  baseURL: config.API_BASE_URL,
  timeout: 10000,
  headers: {
    "Content-Type": "application/json",
  },
});

// Create axios instance for authorized requests
export const axiosAuthorized: AxiosInstance = axios.create({
  baseURL: config.API_BASE_URL,
  timeout: 10000,
  headers: {
    "Content-Type": "application/json",
  },
});

// Token management utilities
export const tokenManager = {
  getAccessToken: (): string | null => {
    return localStorage.getItem(config.TOKEN_STORAGE_KEY);
  },

  getRefreshToken: (): string | null => {
    return localStorage.getItem(config.REFRESH_TOKEN_STORAGE_KEY);
  },

  setTokens: (accessToken: string, refreshToken: string): void => {
    localStorage.setItem(config.TOKEN_STORAGE_KEY, accessToken);
    localStorage.setItem(config.REFRESH_TOKEN_STORAGE_KEY, refreshToken);
  },

  clearTokens: (): void => {
    localStorage.removeItem(config.TOKEN_STORAGE_KEY);
    localStorage.removeItem(config.REFRESH_TOKEN_STORAGE_KEY);
    localStorage.removeItem(config.USER_STORAGE_KEY);
  },

  setUser: (user: any): void => {
    localStorage.setItem(config.USER_STORAGE_KEY, JSON.stringify(user));
  },

  getUser: (): any => {
    const user = localStorage.getItem(config.USER_STORAGE_KEY);
    return user ? JSON.parse(user) : null;
  },
};

// Request interceptor for authorized requests
axiosAuthorized.interceptors.request.use(
  (config: AxiosRequestConfig): any => {
    const token = tokenManager.getAccessToken();
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for handling token refresh
axiosAuthorized.interceptors.response.use(
  (response: AxiosResponse) => {
    return response;
  },
  async (error) => {
    const originalRequest = error.config;

    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      const refreshToken = tokenManager.getRefreshToken();
      if (refreshToken) {
        try {
          const response = await axiosUnauthorized.post(
            `${config.ENDPOINTS.AUTH}/refresh-token`,
            { refreshToken }
          );

          const { accessToken, refreshToken: newRefreshToken } =
            response.data.data;
          tokenManager.setTokens(accessToken, newRefreshToken);

          // Retry the original request with new token
          originalRequest.headers.Authorization = `Bearer ${accessToken}`;
          return axiosAuthorized(originalRequest);
        } catch (refreshError) {
          // Refresh failed, redirect to login
          tokenManager.clearTokens();
          window.location.href = "/login";
          return Promise.reject(refreshError);
        }
      } else {
        // No refresh token, redirect to login
        tokenManager.clearTokens();
        window.location.href = "/login";
      }
    }

    return Promise.reject(error);
  }
);

// Request interceptor for unauthorized requests (just for logging/debugging)
axiosUnauthorized.interceptors.request.use(
  (config: AxiosRequestConfig): any => {
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for unauthorized requests (for error handling)
axiosUnauthorized.interceptors.response.use(
  (response: AxiosResponse) => {
    return response;
  },
  (error) => {
    return Promise.reject(error);
  }
);

export default { axiosAuthorized, axiosUnauthorized };
